import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MapPin, X, Map as MapIcon, QrCode } from "lucide-react";
import { useI18n } from "../lib/i18n";
import DriverQRModal from "./DriverQRModal";

/**
 * Open in Maps — action sheet.
 *
 * Rules (per product spec):
 * - iOS + google_maps_url     → Apple Maps · Google Maps · Mapbox (in-app)
 * - iOS no  google_maps_url   → Apple Maps · Mapbox
 * - Android + google_maps_url → Google Maps · Mapbox
 * - Android no google_maps_url→ Mapbox only
 *
 * Mapbox option always navigates to the in-app /map?focus={id} (works offline).
 * Apple Maps uses the standard `maps://` deep link with coords.
 * Google Maps uses the stored google_maps_url verbatim — NO Google Maps API.
 */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ identifies as Mac with touch — treat as iOS too.
  return /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && "ontouchend" in document);
}

export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

function appleMapsHref(b) {
  // Prefer coords for accuracy; fall back to name search if missing.
  if (b.lat != null && b.lng != null) {
    return `maps://?ll=${b.lat},${b.lng}&q=${encodeURIComponent(b.name || "")}`;
  }
  return `maps://?q=${encodeURIComponent([b.name, b.address].filter(Boolean).join(", "))}`;
}

export default function OpenInMapsSheet({ business, open, onClose, testidPrefix = "open-maps" }) {
  const { t } = useI18n();
  const [driverQrOpen, setDriverQrOpen] = useState(false);
  const ios = isIOS();

  // Esc + scroll lock while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open || !business) return null;

  const showApple = ios; // Apple Maps: iOS only
  // iter78: Google Maps always available — fallback to ?q=lat,lng if no curated URL.
  const googleHref = business.google_maps_url
    || (business.lat != null && business.lng != null
        ? `https://www.google.com/maps?q=${business.lat},${business.lng}`
        : `https://www.google.com/maps?q=${encodeURIComponent([business.name, business.address].filter(Boolean).join(", "))}`);

  // iter78: portal to body so the modal escapes any parent stacking context
  // (e.g. PreviewCard at zIndex 23).
  const sheet = (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid={`${testidPrefix}-sheet`}
      role="dialog"
      aria-label="Open in Maps"
    >
      <div
        className="w-full sm:max-w-sm bg-background border-t sm:border border-gold-soft rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 sm:mx-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <div className="eyebrow text-gold">Open in Maps</div>
          <button
            onClick={onClose}
            data-testid={`${testidPrefix}-close`}
            aria-label="Close"
            className="text-foreground/45 hover:text-gold transition-colors -mt-1 -mr-1 p-1"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="font-display text-2xl text-foreground leading-tight">
          {business.name}
        </div>
        {business.address && (
          <div className="text-xs text-foreground/55 mt-1 truncate">{business.address}</div>
        )}

        <div className="mt-5 space-y-2">
          {showApple && (
            <a
              href={appleMapsHref(business)}
              onClick={onClose}
              data-testid={`${testidPrefix}-apple`}
              className="flex items-center gap-3 rounded-xl border border-gold-soft hover:border-gold hover:bg-gold/[.04] transition-all"
              style={{ height: 56, padding: "0 12px" }}
            >
              <span className="w-8 h-8 rounded-lg bg-foreground/[.06] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-foreground/80" strokeWidth={1.5} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-medium" style={{ color: "#F5F1E8", fontFamily: "'DM Sans', sans-serif" }}>Apple Maps</span>
                <span className="block text-[11px]" style={{ color: "#9A9490", fontFamily: "'DM Sans', sans-serif" }}>{t("open_apple_sub") || "Native iOS directions"}</span>
              </span>
              <span className="text-foreground/35 text-xs">→</span>
            </a>
          )}
          <a
            href={googleHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            data-testid={`${testidPrefix}-google`}
            className="flex items-center gap-3 rounded-xl border border-gold-soft hover:border-gold hover:bg-gold/[.04] transition-all"
            style={{ height: 56, padding: "0 12px" }}
          >
            <span className="w-8 h-8 rounded-lg bg-foreground/[.06] flex items-center justify-center shrink-0">
              <MapIcon className="w-4 h-4 text-gold" strokeWidth={1.5} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[14px] font-medium" style={{ color: "#F5F1E8", fontFamily: "'DM Sans', sans-serif" }}>Google Maps</span>
              <span className="block text-[11px]" style={{ color: "#9A9490", fontFamily: "'DM Sans', sans-serif" }}>{t("open_google_sub") || "Open in Google Maps"}</span>
            </span>
            <span className="text-foreground/35 text-xs">↗</span>
          </a>
          {/* iter77 — Show Driver QR */}
          <button
            onClick={() => setDriverQrOpen(true)}
            data-testid={`${testidPrefix}-driver-qr`}
            className="w-full flex items-center gap-3 rounded-xl border border-gold-soft hover:border-gold hover:bg-gold/[.04] transition-all"
            style={{ height: 56, padding: "0 12px" }}
          >
            <span
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "rgba(232,184,75,0.08)" }}
            >
              <QrCode className="w-4 h-4 text-gold" strokeWidth={1.5} />
            </span>
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-[14px] font-medium" style={{ color: "#F5F1E8", fontFamily: "'DM Sans', sans-serif" }}>
                {t("driver_qr_option_title")}
              </span>
              <span className="block text-[11px]" style={{ color: "#9A9490", fontFamily: "'DM Sans', sans-serif" }}>
                {t("driver_qr_option_sub")}
              </span>
            </span>
            <span className="text-gold text-xs">→</span>
          </button>
        </div>

      </div>
      <DriverQRModal
        business={business}
        open={driverQrOpen}
        onClose={() => setDriverQrOpen(false)}
        testidPrefix={`${testidPrefix}-driver`}
      />
    </div>
  );

  return typeof document !== "undefined" ? createPortal(sheet, document.body) : sheet;
}
